/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { When } from "react-if";
import {
  Box,
  Tab,
  Text,
  theme,
  FlexContainer,
  SVGIcon,
  Tabs,
} from "@nordcloud/gnui";
import { CloudAccountsQueryVariables } from "~/generated/graphql";
import { AlertDotWrap } from "~/components";
import { useCurrency } from "~/hooks";
import { getIcon, isNotNil, Maybe } from "~/tools";
import { CloudAccountsResponse, CloudSummaryTabData } from "../types";
import { getProviderName } from "../utils";
import { CloudAccountTab } from "./CloudAccountTab";
import { Costs } from "./Costs";
import { MovedCosts } from "./MovedCosts";

type Props = {
  activeTab: number;
  setActiveTab: (index: number) => void;
  tabs: CloudSummaryTabData[];
  openFiltersSidebar: () => void;
  searchInput?: Maybe<string>;
  onSetSearchInput: (input: string) => void;
  isLoadingCloudAccounts: boolean;
  data?: Maybe<CloudAccountsResponse>;
  fetchCloudAccountsVariables: CloudAccountsQueryVariables;
  onClear: () => void;
};

export function CloudAccountsTabs({
  activeTab,
  setActiveTab,
  tabs,
  openFiltersSidebar,
  isLoadingCloudAccounts,
  searchInput,
  onSetSearchInput,
  data,
  fetchCloudAccountsVariables,
}: Props) {
  const { currency } = useCurrency();

  const generatedTabs = useMemo(() => {
    return tabs.map(
      ({ name, cost, count, movedToK8s, movedToOc, hasWarning }) => (
        <Tab
          key={name}
          label={
            <span>
              <FlexContainer
                justifyContent="space-between"
                mb={theme.spacing.spacing01}
                css={{ height: "2rem" }}
              >
                <FlexContainer columnGap={theme.spacing.spacing03}>
                  <AlertDotWrap show={hasWarning}>
                    <SVGIcon name={getIcon(name)} />
                  </AlertDotWrap>
                  <Text mb={0} weight="medium">
                    {getProviderName(name)}
                  </Text>
                </FlexContainer>
                <When condition={isNotNil(count)}>
                  <Box
                    boxStyle="lightGrey"
                    innerSpacing="spacing01"
                    css={{ width: "auto" }}
                  >
                    <Text
                      m={0}
                      size="sm"
                      p={`calc(${theme.spacing.spacing01} / 2) ${theme.spacing.spacing02}`}
                    >
                      {count}
                    </Text>
                  </Box>
                </When>
              </FlexContainer>
              <FlexContainer justifyContent="space-between">
                <Costs cost={cost} currency={currency} />
                <MovedCosts
                  currency={currency}
                  movedToK8s={movedToK8s}
                  movedToOc={movedToOc}
                />
              </FlexContainer>
            </span>
          }
        >
          <CloudAccountTab
            fetchCloudAccountsVariables={fetchCloudAccountsVariables}
            openFiltersSidebar={openFiltersSidebar}
            isLoading={isLoadingCloudAccounts}
            searchInput={searchInput}
            data={data}
            movedToK8s={movedToK8s}
            movedToOc={movedToOc}
            hasWarning={hasWarning}
            onSetSearchInput={onSetSearchInput}
          />
        </Tab>
      )
    );
  }, [
    tabs,
    currency,
    searchInput,
    isLoadingCloudAccounts,
    data,
    openFiltersSidebar,
    onSetSearchInput,
  ]);

  return (
    <Tabs step={activeTab} handleTab={setActiveTab}>
      {generatedTabs}
    </Tabs>
  );
}
